import { Select, Input, AlertFloating } from "@light-technology/components";
import { Controller, useController, useForm } from "react-hook-form";
import {
  BatteryManufacturer,
  DeviceType,
  EVSEManufacturer,
  type AppAccount,
  type AppResponse,
  type Manufacturer,
} from "../../state/api.types";
import { manufacturerDisplayNames } from "../shared/manufacturers";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { api } from "../../state/api";
import { FormModal } from "../shared/FormModal";

type AddDeviceForm = {
  manufacturer: Manufacturer | "";
  identifier: string;
  capacity: string;
};

type AddDeviceModalProps = {
  account: AppAccount;
  app: AppResponse;
  onClose: () => void;
  deviceTypes: DeviceType[];
};

const batteryManufacturers = [
  BatteryManufacturer.Tesla,
  BatteryManufacturer.SolarEdge,
  BatteryManufacturer.FoxESS,
  BatteryManufacturer.Generac,
].map((m) => ({ id: m, label: manufacturerDisplayNames[m] }));

const chargerManufacturers = [EVSEManufacturer.Emporia].map((m) => ({
  id: m,
  label: manufacturerDisplayNames[m],
}));

const manufacturerTooltips: Partial<Record<Manufacturer, string>> = {
  [BatteryManufacturer.Tesla]: "Called the energy_site_id in the Tesla API.",
  [BatteryManufacturer.SolarEdge]:
    "SolarEdge: Called the siteId in the SolarEdge API.",
  [BatteryManufacturer.Enphase]:
    "Enphase: Called the system_id in the Enphase API.",
};

export const AddDeviceModal = (props: AddDeviceModalProps) => {
  const isBattery = props.deviceTypes.includes(DeviceType.Battery);

  const { handleSubmit, control, reset, formState } = useForm<AddDeviceForm>({
    defaultValues: {
      manufacturer: "",
      identifier: "",
      capacity: "",
    },
    mode: "onChange",
  });

  const [triggerAddBattery, addBatteryResult] = useMutationWithToast(
    api.useAddBatteryMutation(),
    {
      successMessage: "Device was successfully added.",
      onSuccess: () => {
        reset();
        props.onClose();
      },
    },
  );

  const [triggerAddCharger, addChargerResult] = useMutationWithToast(
    api.useAddChargerMutation(),
    {
      successMessage: "Device was successfully added.",
      onSuccess: () => {
        reset();
        props.onClose();
      },
    },
  );

  const mutationResult = isBattery ? addBatteryResult : addChargerResult;
  const showError = mutationResult.isError;

  const { field: manufacturerField } = useController({
    control,
    name: "manufacturer",
  });
  const selectedManufacturer = manufacturerField.value;

  const deviceLabel = isBattery ? "battery" : "EV charger";
  const title =
    `Add ${deviceLabel} for ` +
    (props.account.first_name
      ? `${props.account.first_name} ${props.account.last_name}`.trim()
      : "user");

  const onSubmit = handleSubmit(({ manufacturer, identifier, capacity }) => {
    // The manufacturer picker is scoped to the device type, so the selected
    // value is always the matching manufacturer enum by the time we submit.
    if (isBattery) {
      triggerAddBattery({
        appUUID: props.app.uuid,
        accountUUID: props.account.uuid,
        manufacturer: manufacturer as BatteryManufacturer,
        identifier,
        preliminary_capacity_kwh: capacity,
      });
    } else {
      triggerAddCharger({
        appUUID: props.app.uuid,
        accountUUID: props.account.uuid,
        manufacturer: manufacturer as EVSEManufacturer,
        identifier,
      });
    }
  });

  return (
    <FormModal
      textTitle={title}
      textCancel="Cancel"
      textSubmit="Save Device"
      isLoading={mutationResult.isLoading}
      isDisabled={!formState.isValid}
      onCancel={props.onClose}
      data-ph-capture-attribute-action="create-device"
      onSubmit={onSubmit}
    >
      <div className="flex flex-col gap-2xl">
        <Controller
          control={control}
          name="manufacturer"
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              label="Manufacturer"
              isRequired={true}
              items={isBattery ? batteryManufacturers : chargerManufacturers}
              onChange={(v) => {
                field.onChange(v);
              }}
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          )}
        />
        <div>
          <Controller
            control={control}
            name="identifier"
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} label="Identifier" isRequired={true} />
            )}
          />
          <div className="text-sm text-tertiary">
            {selectedManufacturer
              ? (manufacturerTooltips[selectedManufacturer] ?? "")
              : ""}
          </div>
        </div>
        {isBattery && (
          <div className="relative">
            <Controller
              control={control}
              name="capacity"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  label="Total capacity"
                  isRequired={true}
                />
              )}
            />
            <span className="pointer-events-none absolute right-3 bottom-2.5 text-md text-tertiary">
              kWh
            </span>
          </div>
        )}
        {showError && (
          <AlertFloating
            color="error"
            confirmLabel=""
            description="We were unable to add a device at this time. Please try again later."
            onClose={() => {
              mutationResult.reset();
            }}
            onConfirm={() => {
              /* no-op */
            }}
            showDismiss={false}
            title="Error occurred"
            truncateDescription={false}
          />
        )}
      </div>
    </FormModal>
  );
};
